/**
 * Stage 8–9 professional curriculum expansion.
 * 30 focused classes complete notation, ear, pedal, repertoire, all-key
 * technique, composition and recital outcomes without bloating earlier stages.
 */

import type {
  AssessmentMode,
  ClassKind,
  ClassSegment,
  TutorClass,
  TutorStage,
} from "./types.js";
import { assessmentForKind } from "./kindAssessment.js";

type ClassSeed = {
  stage: 8 | 9;
  number: number;
  title: string;
  kind: ClassKind;
  minutes?: number;
  description: string;
  skills: string[];
  assessment?: AssessmentMode;
  segments: ClassSegment[];
};

function cls(s: ClassSeed): TutorClass {
  const d = s.minutes ?? 40;
  return {
    id: `class-${s.stage}-${s.number}`,
    stage: s.stage,
    code: `${s.stage}.${s.number}`,
    title: s.title,
    kind: s.kind,
    defaultMinutes: d,
    minMinutes: Math.max(15, Math.round(d * 0.5)),
    maxMinutes: Math.round(d * 1.5),
    description: s.description,
    skills: s.skills,
    assessment: s.assessment ?? assessmentForKind(s.kind),
    segments: s.segments,
  };
}

export const STAGE_8_CLASSES: TutorClass[] = [
  cls({
    stage: 8,
    number: 1,
    title: "Complete Note-Value Reading",
    kind: "theory",
    description: "Rests, dotted values, ties and accurate releases on the beat grid.",
    skills: ["rests", "dotted notes", "ties", "duration reading"],
    assessment: "sight",
    segments: [
      { pieceId: "notation-rests-dots-ties", baseLoops: 5, label: "Read & count", focus: "timing" },
      { pieceId: "rhythm-compound-meters", baseLoops: 3, bpm: 66, metronomeLocked: true, label: "Meter check", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 2,
    title: "Articulation Language",
    kind: "theory",
    description: "Slurs, staccato, tenuto and accents translated into touch.",
    skills: ["slurs", "staccato", "tenuto", "accents"],
    assessment: "sight",
    segments: [
      { pieceId: "notation-articulation-study", baseLoops: 6, label: "Touch contrasts", focus: "articulation" },
      { pieceId: "expression-dynamics-terraces", baseLoops: 3, label: "Accent control", focus: "dynamics" },
    ],
  }),
  cls({
    stage: 8,
    number: 3,
    title: "Bass, Treble & Ledger Fluency",
    kind: "sight",
    description: "Two-staff reading across ledger lines and a real key signature.",
    skills: ["bass clef", "treble clef", "ledger lines", "key signatures"],
    assessment: "sight",
    segments: [
      { pieceId: "notation-ledger-key-signatures", baseLoops: 6, label: "Two-staff span", focus: "pitch" },
      { pieceId: "sight-unseen-b", baseLoops: 3, bpm: 70, metronomeLocked: true, label: "Cold read", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 4,
    title: "Meters Beyond 4/4",
    kind: "rhythm",
    description: "3/4 and 6/8 pulse hierarchy, subdivision and phrase grouping.",
    skills: ["3/4", "6/8", "compound meter", "subdivision"],
    segments: [
      { pieceId: "rhythm-compound-meters", baseLoops: 6, bpm: 72, metronomeLocked: true, label: "6/8 pulse", focus: "timing" },
      { pieceId: "sight-unseen-a", baseLoops: 4, bpm: 70, metronomeLocked: true, label: "3/4 read", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 5,
    title: "Daily Unseen Sight Reading",
    kind: "sight",
    description: "Fresh one-pass lines: scan, count in and continue through errors.",
    skills: ["sight reading", "continuity", "recovery"],
    assessment: "sight",
    segments: [
      { pieceId: "sight-unseen-a", baseLoops: 3, label: "Unseen A", focus: "pitch" },
      { pieceId: "sight-unseen-b", baseLoops: 3, label: "Unseen B", focus: "pitch" },
      { pieceId: "notation-ledger-key-signatures", baseLoops: 2, bpm: 72, metronomeLocked: true, label: "Timed ledger read", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 6,
    title: "Interval Ear Training",
    kind: "ear",
    description: "Recognise, remember and play intervals from seconds through octaves.",
    skills: ["interval recognition", "aural memory", "echo playing"],
    assessment: "ear",
    segments: [
      { pieceId: "ear-intervals-complete", baseLoops: 6, label: "Hear → play", focus: "memory" },
      { pieceId: "ex-interval-jumps", baseLoops: 4, label: "Interval transfer", focus: "pitch" },
    ],
  }),
  cls({
    stage: 8,
    number: 7,
    title: "Chord & Cadence Ear Training",
    kind: "ear",
    description: "Major/minor/diminished colour and V–I arrival by ear.",
    skills: ["chord quality", "cadence recognition", "harmonic hearing"],
    assessment: "ear",
    segments: [
      { pieceId: "ear-chord-cadence-colors", baseLoops: 6, label: "Identify & echo", focus: "memory" },
      { pieceId: "chord-i-iv-v-c", baseLoops: 4, label: "Cadence at keyboard", focus: "pitch" },
    ],
  }),
  cls({
    stage: 8,
    number: 8,
    title: "Sustain Pedal Fundamentals",
    kind: "pedal",
    description: "Press after attack, clear before harmony muddies, then re-catch.",
    skills: ["CC64 sustain pedal", "syncopated pedal", "clean harmony"],
    assessment: "pedal",
    segments: [
      { pieceId: "pedal-change-foundations", baseLoops: 7, bpm: 58, label: "Pedal changes", focus: "pedal" },
      { pieceId: "chord-i-iv-v-c", baseLoops: 3, label: "Harmony without blur", focus: "pedal" },
    ],
  }),
  cls({
    stage: 8,
    number: 9,
    title: "Pedal Legato & Clarity",
    kind: "pedal",
    description: "Connect lyrical phrases while keeping bass and harmony transparent.",
    skills: ["pedal legato", "pedal release", "melody balance"],
    assessment: "pedal",
    segments: [
      { pieceId: "pedal-legato-advanced", baseLoops: 6, label: "Legato changes", focus: "pedal" },
      { pieceId: "repertoire-romantic-lyric", baseLoops: 3, label: "Apply in music", focus: "pedal" },
    ],
  }),
  cls({
    stage: 8,
    number: 10,
    title: "Dynamics & Tone Control",
    kind: "performance",
    description: "Make pp, mf and ff distinct without losing rhythmic control.",
    skills: ["velocity control", "crescendo", "decrescendo", "tone"],
    assessment: "timed",
    segments: [
      { pieceId: "expression-dynamics-terraces", baseLoops: 7, bpm: 72, metronomeLocked: true, label: "Dynamic bands", focus: "dynamics" },
      { pieceId: "dynamics-soft-loud-c", baseLoops: 3, label: "Transfer", focus: "dynamics" },
    ],
  }),
  cls({
    stage: 8,
    number: 11,
    title: "Phrasing, Breathing & Voicing",
    kind: "performance",
    description: "Shape a melodic line and keep accompaniment deliberately softer.",
    skills: ["phrasing", "voicing", "balance", "breathing"],
    assessment: "recital",
    segments: [
      { pieceId: "expression-phrase-voicing", baseLoops: 6, label: "Sing the top voice", focus: "dynamics" },
      { pieceId: "both-ode-with-bass", baseLoops: 3, label: "Balance transfer", focus: "dynamics" },
    ],
  }),
  cls({
    stage: 8,
    number: 12,
    title: "Ornaments Without Tension",
    kind: "drill",
    description: "Measured trills, mordents and turns before expressive placement.",
    skills: ["trill", "mordent", "turn", "relaxed rotation"],
    segments: [
      { pieceId: "ornaments-trill-mordent-turn", baseLoops: 7, bpm: 60, metronomeLocked: true, label: "Measured ornaments", focus: "timing" },
      { pieceId: "etude-c-ascending-break", baseLoops: 3, label: "Relaxed passagework", focus: "articulation" },
    ],
  }),
  cls({
    stage: 8,
    number: 13,
    title: "Baroque Counterpoint Miniature",
    kind: "repertoire",
    minutes: 45,
    description: "Complete two-voice miniature: articulation, imitation and independence.",
    skills: ["counterpoint", "baroque style", "voice independence"],
    assessment: "recital",
    segments: [
      { pieceId: "repertoire-petzold-minuet-g-complete", baseLoops: 4, bpm: 112, label: "Petzold Minuet — complete score", focus: "articulation" },
      { pieceId: "repertoire-baroque-two-voice", baseLoops: 3, bpm: 80, metronomeLocked: true, label: "Two-voice transfer", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 14,
    title: "Classical Sonatina Miniature",
    kind: "repertoire",
    minutes: 45,
    description: "Complete classical-form study with Alberti bass and balanced cadences.",
    skills: ["classical style", "Alberti bass", "phrase balance"],
    assessment: "recital",
    segments: [
      { pieceId: "repertoire-classical-alberti", baseLoops: 7, label: "Hands & phrases", focus: "dynamics" },
      { pieceId: "repertoire-classical-alberti", baseLoops: 3, bpm: 92, metronomeLocked: true, label: "Complete take", focus: "timing" },
    ],
  }),
  cls({
    stage: 8,
    number: 15,
    title: "Romantic Recital & Stage Review",
    kind: "performance",
    minutes: 50,
    description: "Complete lyrical work plus notation, ear and expression review.",
    skills: ["romantic style", "pedal", "voicing", "recital continuity"],
    assessment: "recital",
    segments: [
      { pieceId: "repertoire-romantic-lyric", baseLoops: 6, label: "Lyrical work", focus: "pedal" },
      { pieceId: "expression-phrase-voicing", baseLoops: 3, label: "Voicing check", focus: "dynamics" },
      { pieceId: "sight-unseen-b", baseLoops: 2, bpm: 76, metronomeLocked: true, label: "Unseen exit", focus: "timing" },
    ],
  }),
];

export const STAGE_9_CLASSES: TutorClass[] = [
  cls({
    stage: 9,
    number: 1,
    title: "Major Scales — Sharp Side",
    kind: "scale",
    description: "C, G, D, A, E and B major with consistent fingering and pulse.",
    skills: ["major scales", "sharp keys", "fingering"],
    assessment: "timed",
    segments: [
      { pieceId: "tech-major-scales-sharp-keys", baseLoops: 6, bpm: 84, metronomeLocked: true, label: "Sharp-key circuit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 2,
    title: "Major Scales — Flat Side",
    kind: "scale",
    description: "F, B-flat, E-flat, A-flat, D-flat and G-flat major.",
    skills: ["major scales", "flat keys", "fingering"],
    assessment: "timed",
    segments: [
      { pieceId: "tech-major-scales-flat-keys", baseLoops: 6, bpm: 80, metronomeLocked: true, label: "Flat-key circuit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 3,
    title: "Natural Minor — All Keys",
    kind: "scale",
    description: "Twelve natural minor scales with key-awareness, not rote shape alone.",
    skills: ["natural minor", "all 12 keys", "fingering"],
    segments: [
      { pieceId: "tech-minor-scales-natural", baseLoops: 5, label: "12-key circuit", focus: "pitch" },
    ],
  }),
  cls({
    stage: 9,
    number: 4,
    title: "Harmonic & Melodic Minor — All Keys",
    kind: "scale",
    minutes: 45,
    description: "Raised leading tones and melodic-minor upper tetrachords in every key.",
    skills: ["harmonic minor", "melodic minor", "all 12 keys"],
    assessment: "timed",
    segments: [
      { pieceId: "tech-minor-scales-harmonic", baseLoops: 4, label: "Harmonic circuit", focus: "pitch" },
      { pieceId: "tech-minor-scales-melodic", baseLoops: 4, bpm: 76, metronomeLocked: true, label: "Melodic circuit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 5,
    title: "Arpeggios in Every Key",
    kind: "scale",
    description: "Major/minor arpeggios around the chromatic circle with relaxed crossings.",
    skills: ["major arpeggios", "minor arpeggios", "all keys"],
    assessment: "timed",
    segments: [
      { pieceId: "tech-arpeggios-all-keys", baseLoops: 6, bpm: 80, metronomeLocked: true, label: "Arpeggio circuit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 6,
    title: "Chromatic Scale & Broken Thirds",
    kind: "speed",
    description: "Finger substitution, half-step evenness and third-pattern control.",
    skills: ["chromatic scale", "broken thirds", "finger substitution"],
    segments: [
      { pieceId: "tech-chromatic-double-thirds", baseLoops: 7, bpm: 88, metronomeLocked: true, label: "Chromatic + thirds", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 7,
    title: "Independence Circuit",
    kind: "drill",
    description: "Original five-finger transposition exercise for evenness in both hands.",
    skills: ["finger independence", "evenness", "transposition"],
    segments: [
      { pieceId: "tech-hanon-pattern-original", baseLoops: 8, bpm: 88, metronomeLocked: true, label: "Evenness circuit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 8,
    title: "Classical Velocity Etude",
    kind: "speed",
    description: "Original classical passagework for rotation, coordination and clean speed.",
    skills: ["velocity", "rotation", "two-hand coordination"],
    assessment: "timed",
    segments: [
      { pieceId: "tech-czerny-velocity-original", baseLoops: 7, bpm: 96, metronomeLocked: true, label: "Controlled speed", focus: "timing" },
      { pieceId: "both-speed-alternation", baseLoops: 3, bpm: 104, metronomeLocked: true, label: "Alternation exit", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 9,
    title: "Advanced Pedal & Colour",
    kind: "pedal",
    description: "Pedal-led voicing, frequent changes and clean release under lyrical texture.",
    skills: ["advanced pedal", "colour", "clarity", "voicing"],
    assessment: "pedal",
    segments: [
      { pieceId: "pedal-legato-advanced", baseLoops: 4, label: "Pedal review", focus: "pedal" },
      { pieceId: "performance-recital-b", baseLoops: 4, label: "Recital pedal", focus: "pedal" },
    ],
  }),
  cls({
    stage: 9,
    number: 10,
    title: "Advanced Unseen Reading",
    kind: "sight",
    description: "Cold two-staff reading in E-flat with continuity and recovery.",
    skills: ["advanced sight reading", "flat keys", "continuity"],
    assessment: "sight",
    segments: [
      { pieceId: "sight-transposition-challenge", baseLoops: 5, label: "Cold read", focus: "pitch" },
      { pieceId: "sight-unseen-b", baseLoops: 2, bpm: 82, metronomeLocked: true, label: "Speed read", focus: "timing" },
    ],
  }),
  cls({
    stage: 9,
    number: 11,
    title: "Melodic Dictation & Memory",
    kind: "ear",
    description: "Hold and reproduce longer phrases with leaps and minor colour.",
    skills: ["melodic dictation", "aural memory", "intervals"],
    assessment: "ear",
    segments: [
      { pieceId: "ear-transcription-advanced", baseLoops: 7, label: "Hear → retain → play", focus: "memory" },
      { pieceId: "ear-chord-cadence-colors", baseLoops: 3, label: "Cadence recall", focus: "memory" },
    ],
  }),
  cls({
    stage: 9,
    number: 12,
    title: "Improvisation: Motif to Phrase",
    kind: "improvisation",
    description: "Build sequences and answers from a small motif over stable harmony.",
    skills: ["improvisation", "motif", "sequence", "phrase"],
    segments: [
      { pieceId: "improv-motif-variation", baseLoops: 7, label: "Model variations", focus: "memory" },
      { pieceId: "prog-ii-v-i-c", baseLoops: 3, label: "Harmony canvas", focus: "pitch" },
    ],
  }),
  cls({
    stage: 9,
    number: 13,
    title: "Composition & Harmony Lab",
    kind: "theory",
    description: "Write/play question-answer phrases over functional I–IV–V–I harmony.",
    skills: ["composition", "harmonic function", "cadence", "form"],
    segments: [
      { pieceId: "theory-harmony-composition", baseLoops: 7, label: "Phrase model", focus: "pitch" },
      { pieceId: "circle-fifths-triads", baseLoops: 3, label: "Key relationships", focus: "pitch" },
    ],
  }),
  cls({
    stage: 9,
    number: 14,
    title: "Mock Recital & Recovery",
    kind: "performance",
    minutes: 50,
    description: "Two uninterrupted complete works; recover without restarting after errors.",
    skills: ["performance", "continuity", "recovery", "style contrast"],
    assessment: "recital",
    segments: [
      { pieceId: "performance-recital-a", baseLoops: 4, label: "Recital I", focus: "articulation" },
      { pieceId: "performance-recital-b", baseLoops: 4, label: "Recital II", focus: "dynamics" },
    ],
  }),
  cls({
    stage: 9,
    number: 15,
    title: "Final Recorded Recital",
    kind: "performance",
    minutes: 60,
    description: "Final complete capstone scored for pitch, timing, dynamics and continuity.",
    skills: ["final recital", "recording", "performance", "musicianship"],
    assessment: "recital",
    segments: [
      { pieceId: "tech-major-scales-sharp-keys", baseLoops: 1, bpm: 88, metronomeLocked: true, label: "Technique check", focus: "timing" },
      { pieceId: "performance-recital-a", baseLoops: 2, label: "Style check", focus: "articulation" },
      { pieceId: "performance-final-capstone", baseLoops: 5, bpm: 88, metronomeLocked: true, label: "Final recorded take", focus: "dynamics" },
    ],
  }),
];

export const MASTERY_STAGES: TutorStage[] = [
  {
    id: 8,
    title: "Reading, Ear & Expressive Repertoire",
    blurb: "Professional musicianship — notation, sight, ear, pedal, expression and complete miniatures.",
    examId: "exam-stage-8",
    classes: STAGE_8_CLASSES,
  },
  {
    id: 9,
    title: "Complete Technique & Final Recital",
    blurb: "All-key technique, advanced musicianship, composition and a recorded final recital.",
    examId: "exam-stage-9",
    classes: STAGE_9_CLASSES,
  },
];

export interface PracticePack {
  id: string;
  title: string;
  description: string;
  pieceIds: string[];
}

/** Optional packs do not block exams/certificate; they deepen weak skills. */
export const PRACTICE_PACKS: PracticePack[] = [
  {
    id: "pack-reading",
    title: "Daily Reading Lab",
    description: "Fresh notation, ledger-line and two-staff reading.",
    pieceIds: ["notation-rests-dots-ties", "sight-unseen-a", "sight-unseen-b", "sight-transposition-challenge"],
  },
  {
    id: "pack-technique",
    title: "12-Key Technique Circuit",
    description: "All major/minor scales, arpeggios, chromatic work and evenness.",
    pieceIds: ["tech-major-scales-sharp-keys", "tech-major-scales-flat-keys", "tech-minor-scales-harmonic", "tech-arpeggios-all-keys", "tech-hanon-pattern-original"],
  },
  {
    id: "pack-ear",
    title: "Ear & Memory Lab",
    description: "Intervals, chords, cadences and melodic dictation.",
    pieceIds: ["ear-intervals-complete", "ear-chord-cadence-colors", "ear-transcription-advanced"],
  },
  {
    id: "pack-repertoire",
    title: "Classical Repertoire Miniatures",
    description: "A verified public-domain Minuet plus complete Baroque, Classical and Romantic pedagogical works.",
    pieceIds: ["repertoire-petzold-minuet-g-complete", "repertoire-baroque-two-voice", "repertoire-classical-alberti", "repertoire-romantic-lyric"],
  },
  {
    id: "pack-performance",
    title: "Recital Preparation",
    description: "Full takes, style contrast, recovery and final capstone.",
    pieceIds: ["performance-recital-a", "performance-recital-b", "performance-final-capstone"],
  },
];
